/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {CdnUploadApiService, CdnUploadModel} from './cdn-upload-api.service';
import {CdnUploadEditPageComponent} from './cdn-upload-edit-page.component';
import {CdnUploadRoutePageComponent} from './cdn-upload-route-page.component';

export function cdnUploadStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(CdnUploadApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const rh = adnRouterHelper.createRouterHelper(
    'cdn-uploads.cdn-upload',
    CdnUploadApiService,
    'app.adm',
    'app/admin'
  );

  states.add(rh.listPath(), rh.list({
    views: {
      'content@app': {
        component: CdnUploadRoutePageComponent,
        bindings: {readOnly: 'listReadOnly'}
      },
      'context@app': {
        component: ListDefaultActionsComponent,
        bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}
      },
      'title@app': stateTitleView()
    }
  }));

  states.add(rh.newItemPath(), rh.newItem());

  states.add(rh.itemPath(), rh.item({
    resolve: {
      cdnUploadPage: function($stateParams: any, readOnly: boolean) {
        if ($stateParams.isNew) {
          const model: CdnUploadModel = {
            id: $stateParams.id,
            objectState: 'ACTIVE',
            validationWarnings: []
          };
          return new SignalAdminResourcePageState(model, true, !!readOnly, api, 'CDN Upload', 'fileName');
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model,
          false,
          !!readOnly,
          api,
          'CDN Upload',
          'fileName'
        ));
      },
      model: function(cdnUploadPage: SignalAdminResourcePageState<CdnUploadModel>) {
        return cdnUploadPage.model();
      }
    },
    views: {
      'content@app': {
        component: CdnUploadEditPageComponent,
        bindings: {state: 'cdnUploadPage'}
      },
      'context@app': {
        component: SignalAdminResourceContextActionsComponent,
        bindings: {state: 'cdnUploadPage'}
      },
      'title@app': {
        component: SignalAdminResourceTitleComponent,
        bindings: {state: 'cdnUploadPage'}
      }
    }
  }));

  return states.build();
}
